import React, { useCallback, useEffect } from 'react'
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOnExit } from 'react-plaid-link'

import { useTranslation } from 'next-i18next'

import plaidApi from 'redux/services/plaid'

import { RootState, TypedDispatch } from 'redux/store'
import { useDispatch } from 'react-redux'

import { setPaymentMethod } from 'redux/features/paymentDetails'

import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'

import Bugsnag from '@bugsnag/js'
import { trackFormSubmitEvent } from 'modules/analytics/helpers/track-form-events'
import useModal from '../../../../modal/state/use-modal'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import { PaymentSelectionButtonProps } from './payment-selection-button'
import PaymentActionButton from './payment-action-button'
import useFlow from 'modules/flow/state/use-flow'
import { getHeapUserId } from 'modules/analytics/plugins/heap'
import { getClassySessionId } from 'modules/utils/getClassySessionId'
import { PaymentMethod } from 'models/ChargeIntent'
import {
  validateForms,
  saveFormsData,
} from '../../steps/single/utils/single-step-form-orchestrator'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'

import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'
import { getCookieValue } from 'modules/utils/getCookieValue'

export const PlaidButton = (props: PaymentSelectionButtonProps) => {
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const modal = useModal()
  const status = useStatus()

  const trackEvent = useTrackUIElementEvent()

  const { t } = useTranslation()

  const campaignId = config.campaignId
  const currency = order.currency.value

  const [createLinkToken, { data: linkData }] = plaidApi.useCreateLinkTokenMutation()

  useEffect(() => {
    createLinkToken({ memberId: getHeapUserId() || '' })
  }, [createLinkToken])

  const { data: tokenData } = plaidApi.useGetPlaidTokenQuery(
    { campaignId, currency: currency ?? 'USD' } as { campaignId: string; currency: string },
    { skip: !campaignId },
  )

  // this really just sets all of these to the empty string however if the
  // donor backtracks after entering these in the DonorInfo form these will have
  // actual values - thus we won't overwrite them in the onSuccess callback below
  const city = order.address.value.city
  const line1 = order.address.value.line1
  const line2 = order.address.value.line2 ?? ''
  const zipCode = order.address.value.zipCode
  const state = order.address.value.state

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onExit = useCallback<PlaidLinkOnExit>((error, _metadata) => {
    if (error) {
      Bugsnag.notify(`PlaidButton onExit error in PlaidButton/index.tsx: ${error}`)
      console.log('error:', error)
    }
  }, [])

  const dispatch = useDispatch<TypedDispatch<RootState>>()

  const onSuccess = useCallback<PlaidLinkOnSuccess>((token, metadata) => {
    // APIv2 expects these to be non-null but Plaid doesn't collect them
    order.address.setValue({
      city,
      state,
      country: 'US', // Only US bank accounts work with ACH
      zipCode,
      line1,
      line2,
    })

    // Extract `account_id` safely
    const accountId = metadata.accounts.length > 0 ? metadata.accounts[0].id : ''

    dispatch(setPaymentMethod({ publicToken: token, accountId }))

    trackEvent('Donation Payment Details Submitted', mParticleSdk.EventType.Other)

    /**
     * Only show the modal for new checkout flow
     */
    if (!config.isEmbedded) {
      modal.open('missing-digital-payment-info-fallback')
    } else if (flow.nextStep) {
      flow.goToStep(flow.nextStep.name)
    }

    if (window.newrelic) {
      window.newrelic.addPageAction('payment_method_selected', {
        pgdid: getCookieValue('pgdid'),
        'classy-session-id': getClassySessionId(),
        paymentMethod: PaymentMethod.Plaid,
      })
    }
  }, [])

  // selectAccount is usable, but deprecated & stripped from types: https://github.com/plaid/react-plaid-link/issues/266
  let plaidConfig: Partial<Parameters<typeof usePlaidLink>[0] & { selectAccount: boolean }> = {}
  let ready = false

  if (tokenData && linkData) {
    plaidConfig = {
      token: linkData?.linkToken,
      env: tokenData.environment,
      clientName: t('PlaidButton.plaid.clientName', 'Classy Pay'),
      publicKey: tokenData.publicKey,
      product: ['auth', 'identity'],
      selectAccount: true,
      onSuccess,
      onExit,
    }
  }

  const plaidOptions = usePlaidLink(plaidConfig as Parameters<typeof usePlaidLink>[0])
  ready = plaidOptions?.ready

  const handleClick = async () => {
    trackFormSubmitEvent(flow.currentStep.name)

    try {
      status.set(StatusType.SUBMITTING)

      // Validate all forms and save data before opening Plaid modal
      if (!config.isEmbedded) {
        const validationResult = await validateForms()

        if (!validationResult.success) {
          throw new Error(validationResult.error || 'Please fill in all required fields')
        }

        saveFormsData(validationResult.results || [], order)
      }

      status.set(StatusType.IDLE)

      // Now open Plaid modal
      plaidOptions?.open()
    } catch (error) {
      console.error('Error validating custom questions:', error)
      status.set(StatusType.FAILED, 'An error occurred while validating the form')
    }
  }

  return (
    <>
      {!ready && <Placeloader style={{ width: '100%', height: 54, borderRadius: 12 }} />}
      {ready && (
        <PaymentActionButton onClick={handleClick} {...props}>
          {props.paymentMethodConfig.actionButtonText}
        </PaymentActionButton>
      )}
    </>
  )
}
